import { forwardRef, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Package, Truck, ListChecks, MapPin, Loader2, CreditCard, FileText, AlertTriangle, CalendarClock, Info, Pencil, Plus, X } from "lucide-react";
import RecordEditDialog from "@/components/admin/RecordEditDialog";
import { orderFields, orderCreateFields } from "@/components/admin/recordFields";
import AdminLayout from "@/components/admin/AdminLayout";
import { deliveryWindowLabel, anchorFromPlannedDate, upcomingDepartures } from "@/lib/delivery-dates";
import { products as PRODUCTS } from "@/data/pricing";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

type OrderItem = { product_id: string; name?: string; quantity: number };

type ShippingAddress = {
  name?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  country?: string;
};

type DeliveryRow = {
  id: string;
  user_id: string;
  subscription_id: string | null;
  product_name: string;
  quantity: number;
  status: string;
  order_mode: string | null;
  payment_method: string | null;
  payment_status: string | null;
  created_at: string;
  items: OrderItem[] | null;
  shipping_address: ShippingAddress | null;
  planned_delivery_date: string | null;
  /** Beräknad kommande leverans — finns inte som order i databasen. */
  is_forecast?: boolean;
};

type ProfileLite = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
};

type SubInfo = {
  id: string;
  user_id: string;
  nickname: string | null;
  status: string;
  cancel_at_period_end: boolean | null;
  next_delivery_date: string | null;
  paused_until: string | null;
  tier_tubes: number;
  payment_method_type: string | null;
};

type MissingDeliveryRow = {
  subscription_id: string;
  user_id: string;
  nickname: string | null;
  tier_tubes: number;
  next_delivery_date: string | null;
};

type AwaitingPaymentRow = {
  order_id: string;
  subscription_id: string;
  user_id: string;
  nickname: string | null;
  tier_tubes: number;
  next_delivery_date: string | null;
  payment_method: string | null;
  /** Fakturan är makulerad — vi väntar alltså inte på någon betalning. */
  invoice_voided?: boolean;
};

const fmtDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("sv-SE", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

const fmtMonthLabel = (iso: string) =>
  new Date(iso + "T00:00:00").toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "long",
  });

const monthKey = (iso: string | null): string | null => {
  if (!iso) return null;
  return iso.slice(0, 7); // YYYY-MM
};

const todayMonthKey = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const nextMonthKey = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const badgeFor = (s: string) => {
  switch (s) {
    case "shipped":
      return "bg-emerald-100 text-emerald-800 hover:bg-emerald-100";
    case "pending":
      return "bg-amber-100 text-amber-800 hover:bg-amber-100";
    default:
      return "bg-slate-100 text-slate-800 hover:bg-slate-100";
  }
};

/** Smakens färgkula + kortnamn i packvyn. Färgen plockas ur produktens gradient. */
const FLAVOR_DOT: Record<string, { label: string; color: string }> = {};
PRODUCTS.forEach((p) => {
  const m = p.gradient.match(/to-\[(#[0-9a-fA-F]{6})\]/);
  FLAVOR_DOT[p.id] = { label: p.shortName, color: m?.[1] ?? "#94a3b8" };
});

/** "ons 1 okt" — kompakt datum för packkorten. */
const fmtDay = (iso: string) =>
  new Date(iso + "T00:00:00").toLocaleDateString("sv-SE", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

type TabKey = "pending" | "shipped" | "all" | "flagged";
type MonthFilter = "current" | "next" | "all";

type FlaggedPanelProps = {
  missing: MissingDeliveryRow[];
  awaiting: AwaitingPaymentRow[];
  orphans: DeliveryRow[];
  profiles: Record<string, ProfileLite>;
};

const FlaggedPanel = forwardRef<HTMLDivElement, FlaggedPanelProps>(
  ({ missing, awaiting, orphans, profiles }, ref) => {
    if (missing.length === 0 && awaiting.length === 0 && orphans.length === 0) {
      return (
        <Card ref={ref} className="p-10 text-center text-sm text-muted-foreground">
          Inga flaggade leveranser just nu — allt rullar på som det ska.
        </Card>
      );
    }
    return (
      <div ref={ref} className="space-y-4">
      {orphans.length > 0 && (
        <Card className="border-red-200 bg-red-50/60 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-medium text-red-900">
                {orphans.length} leverans{orphans.length === 1 ? "" : "er"} på uppsagt abonnemang
              </div>
              <div className="text-sm text-red-800/90 mt-1">
                Abonnemanget är uppsagt men leveransen ligger kvar som väntande. Avbryt den om den inte ska skickas.
              </div>
              <ul className="mt-3 space-y-1.5 text-sm">
                {orphans.map((o) => {
                  const p = profiles[o.user_id];
                  const customer = p
                    ? [p.first_name, p.last_name].filter(Boolean).join(" ").trim() || p.email
                    : "—";
                  return (
                    <li key={o.id} className="text-red-900">
                      <Link to={`/admin/customers/${o.user_id}`} className="font-medium hover:underline">
                        {customer}
                      </Link>
                      <span className="text-red-700/80"> · {o.product_name} · {o.quantity} tuber</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {missing.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/60 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-medium text-amber-900">
                {missing.length} aktiv{missing.length === 1 ? "" : "a"} prenumeration{missing.length === 1 ? "" : "er"} saknar planerad leverans
              </div>
              <div className="text-sm text-amber-800/90 mt-1">
                Ingen väntande order finns. Kontrollera prenumerationen och skapa en order vid behov.
              </div>
              <ul className="mt-3 space-y-1.5 text-sm">
                {missing.map((m) => {
                  const p = profiles[m.user_id];
                  const customer = p
                    ? [p.first_name, p.last_name].filter(Boolean).join(" ").trim() || p.email
                    : "—";
                  return (
                    <li key={m.subscription_id} className="text-amber-900">
                      <Link to={`/admin/customers/${m.user_id}`} className="font-medium hover:underline">
                        {customer}
                      </Link>
                      {m.nickname && <span className="text-amber-800"> · {m.nickname}</span>}
                      <span className="text-amber-700/80"> · {m.tier_tubes} tuber</span>
                      {m.next_delivery_date && (
                        <span className="text-amber-700/80"> · planerad {fmtDate(m.next_delivery_date)}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {awaiting.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/60 p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-medium text-blue-900">
                {awaiting.length} anläggning{awaiting.length === 1 ? "" : "ar"} väntar på kundbetalning
              </div>
              <div className="text-sm text-blue-800/90 mt-1">
                Leveransen planeras automatiskt så snart kunden slutfört fakturabetalningen — ingen åtgärd från admin behövs.
              </div>
              <ul className="mt-3 space-y-1.5 text-sm">
                {awaiting.map((a) => {
                  const p = profiles[a.user_id];
                  const customer = p
                    ? [p.first_name, p.last_name].filter(Boolean).join(" ").trim() || p.email
                    : "—";
                  return (
                    <li key={a.order_id} className="text-blue-900">
                      <Link to={`/admin/customers/${a.user_id}`} className="font-medium hover:underline">
                        {customer}
                      </Link>
                      {a.nickname && <span className="text-blue-800"> · {a.nickname}</span>}
                      <span className="text-blue-700/80"> · {a.tier_tubes} tuber</span>
                      {a.next_delivery_date && (
                        <span className="text-blue-700/80"> · planerad {fmtDate(a.next_delivery_date)}</span>
                      )}
                      {a.invoice_voided && (
                        <Badge
                          variant="secondary"
                          className="ml-2 bg-rose-100 text-rose-900 hover:bg-rose-100 text-[10px] font-normal"
                        >
                          Faktura makulerad – ingen betalning väntas
                        </Badge>
                      )}
                      <Link
                        to={`/admin/invoices?focus=${a.order_id}`}
                        className="ml-2 text-blue-700 underline-offset-2 hover:underline"
                      >
                        {a.invoice_voided ? "Skicka ny faktura" : "Skapa & skicka faktura"}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </Card>
      )}
      </div>
    );
  },
);
FlaggedPanel.displayName = "FlaggedPanel";

const AdminDeliveriesPage = () => {
  const [orders, setOrders] = useState<DeliveryRow[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileLite>>({});
  const [subs, setSubs] = useState<Record<string, SubInfo>>({});
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<TabKey>("pending");
  const [monthFilter, setMonthFilter] = useState<MonthFilter>("current");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [shippingIds, setShippingIds] = useState<Set<string>>(new Set());
  const [packingOpen, setPackingOpen] = useState(false);
  const [allPendingSubIds, setAllPendingSubIds] = useState<Set<string>>(new Set());
  const [awaitingPayment, setAwaitingPayment] = useState<AwaitingPaymentRow[]>([]);
  const [editOrder, setEditOrder] = useState<Record<string, unknown> | null>(null);
  const [manualOrderOpen, setManualOrderOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [voidedOrderIds, setVoidedOrderIds] = useState<Set<string>>(new Set());
  /** `subscription_id|YYYY-MM` för månader vars faktura är makulerad utan ersättare. */
  const [voidedSubMonths, setVoidedSubMonths] = useState<Set<string>>(new Set());

  const load = async () => {
    setLoading(true);
    const [ordersRes, subsRes, allPendingRes, voidedInvRes] = await Promise.all([
      supabase
        .from("orders")
        .select(
          "id, user_id, subscription_id, product_name, quantity, status, order_mode, payment_method, payment_status, created_at, items, shipping_address",
        )
        .not("payment_status", "in", "(awaiting_payment,cancelled,failed)")
        .order("created_at", { ascending: false })
        .limit(500),
      supabase
        .from("subscriptions")
        .select("id, user_id, nickname, status, cancel_at_period_end, next_delivery_date, paused_until, tier_tubes, payment_method_type"),

      supabase
        .from("orders")
        .select("id, subscription_id, user_id, payment_status, payment_method")
        .eq("status", "pending")
        .not("subscription_id", "is", null),

      supabase
        .from("invoices")
        .select("id, order_id, subscription_id, status, period_start, period_end, due_date, created_at"),
    ]);

    type InvLite = {
      order_id: string | null;
      subscription_id: string | null;
      status: string | null;
      period_start: string | null;
      period_end: string | null;
      due_date: string | null;
      created_at: string | null;
    };
    const invoices = (voidedInvRes.data as InvLite[]) || [];
    // Vilken leveransmånad hör fakturan till? Perioden vinner, annars förfallo-
    // datum, annars när fakturan skapades.
    const invMonth = (i: InvLite) =>
      monthKey(i.period_start ?? i.period_end ?? i.due_date ?? i.created_at);

    const voidedOrders = new Set(
      invoices.filter((i) => i.status === "void" && i.order_id).map((i) => i.order_id!),
    );
    // En månad med en giltig faktura (utkast/skickad/betald) räknas aldrig som
    // makulerad — den nya fakturan ersätter den gamla.
    const activeSubMonths = new Set(
      invoices
        .filter((i) => i.status !== "void" && i.subscription_id)
        .map((i) => `${i.subscription_id}|${invMonth(i)}`),
    );
    const voidedMonths = new Set(
      invoices
        .filter((i) => i.status === "void" && i.subscription_id)
        .map((i) => `${i.subscription_id}|${invMonth(i)}`)
        .filter((k) => !activeSubMonths.has(k)),
    );
    setVoidedOrderIds(voidedOrders);
    setVoidedSubMonths(voidedMonths);

    const subList = (subsRes.data as SubInfo[]) || [];
    const subMap: Record<string, SubInfo> = {};
    subList.forEach((s) => (subMap[s.id] = s));
    setSubs(subMap);

    const allPending = (allPendingRes.data as Array<{ id: string; subscription_id: string | null; user_id: string; payment_status: string | null; payment_method: string | null }>) || [];
    setAllPendingSubIds(new Set(allPending.filter((o) => o.subscription_id).map((o) => o.subscription_id!)));

    const awaiting: AwaitingPaymentRow[] = allPending
      .filter((o) => o.payment_status === "awaiting_payment" && o.subscription_id && subMap[o.subscription_id])
      .map((o) => ({
        order_id: o.id,
        subscription_id: o.subscription_id!,
        user_id: o.user_id,
        nickname: subMap[o.subscription_id!]?.nickname ?? null,
        tier_tubes: subMap[o.subscription_id!]?.tier_tubes ?? 0,
        next_delivery_date: subMap[o.subscription_id!]?.next_delivery_date ?? null,
        payment_method: o.payment_method,
        invoice_voided:
          voidedOrders.has(o.id) ||
          (!!o.subscription_id &&
            voidedMonths.has(
              `${o.subscription_id}|${monthKey(subMap[o.subscription_id]?.next_delivery_date ?? null)}`,
            )),
      }));
    setAwaitingPayment(awaiting);

    const rawOrders = (ordersRes.data as Omit<DeliveryRow, "planned_delivery_date">[]) || [];
    const enriched: DeliveryRow[] = rawOrders.map((o) => ({
      ...o,
      // Never null: fall back to the order's own creation date so a delivery
      // can't disappear from every month view.
      planned_delivery_date:
        (o.subscription_id ? subMap[o.subscription_id]?.next_delivery_date : null) ??
        o.created_at.slice(0, 10),
    }));
    setOrders(enriched);

    const userIds = Array.from(
      new Set([...enriched.map((o) => o.user_id), ...subList.map((s) => s.user_id)]),
    );
    if (userIds.length > 0) {
      const { data: ps } = await supabase
        .from("profiles")
        .select("id, email, first_name, last_name, company")
        .in("id", userIds);
      const map: Record<string, ProfileLite> = {};
      ((ps as ProfileLite[]) || []).forEach((p) => {
        map[p.id] = p;
      });
      setProfiles(map);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  // Subscriptions that are active but have NO pending order at all (regardless of payment status) → flag
  const missingDeliveries = useMemo<MissingDeliveryRow[]>(() => {
    return Object.values(subs)
      .filter((s) => s.status === "active" && !allPendingSubIds.has(s.id))
      .map((s) => ({
        subscription_id: s.id,
        user_id: s.user_id,
        nickname: s.nickname,
        tier_tubes: s.tier_tubes,
        next_delivery_date: s.next_delivery_date,
      }));
  }, [allPendingSubIds, subs]);

  const todayIso = new Date().toLocaleDateString("sv-SE");

  const isOverdue = (o: DeliveryRow) =>
    o.status === "pending" &&
    !!o.planned_delivery_date &&
    o.planned_delivery_date < todayIso;

  // Pending deliveries hanging off a cancelled subscription → flag
  const orphanDeliveries = useMemo(
    () =>
      orders.filter(
        (o) =>
          o.status === "pending" &&
          o.subscription_id &&
          subs[o.subscription_id]?.status === "cancelled",
      ),
    [orders, subs],
  );

  // Prognos: kommande avgångar 6 månader framåt för abonnemang som rullar
  // vidare. Skapar inga ordrar — bara en vy av vad som är på väg.
  const forecastRows = useMemo<DeliveryRow[]>(() => {
    const out: DeliveryRow[] = [];
    const existingMonths = new Set(
      orders
        .filter((o) => o.subscription_id)
        .map((o) => `${o.subscription_id}|${monthKey(o.planned_delivery_date)}`),
    );
    Object.values(subs).forEach((s) => {
      if (s.status === "cancelled" || s.cancel_at_period_end) return;
      const anchor = anchorFromPlannedDate(s.next_delivery_date);
      const from =
        s.paused_until && s.paused_until > todayIso ? s.paused_until : todayIso;
      const lastOrder = orders.find((o) => o.subscription_id === s.id);
      for (const dep of upcomingDepartures(from, anchor, 6)) {
        if (existingMonths.has(`${s.id}|${monthKey(dep)}`)) continue;
        out.push({
          id: `forecast-${s.id}-${dep}`,
          user_id: s.user_id,
          subscription_id: s.id,
          product_name: s.nickname ?? lastOrder?.product_name ?? `${s.tier_tubes} tuber`,
          quantity: s.tier_tubes,
          status: "forecast",
          order_mode: "subscription",
          payment_method: s.payment_method_type,
          payment_status: null,
          created_at: dep,
          items: lastOrder?.items ?? null,
          shipping_address: lastOrder?.shipping_address ?? null,
          planned_delivery_date: dep,
          is_forecast: true,
        });
      }
    });
    return out;
  }, [orders, subs, todayIso]);

  const allRows = useMemo(() => [...orders, ...forecastRows], [orders, forecastRows]);

  // Apply month filter on planned_delivery_date — overdue pending deliveries
  // are always included so nothing can hide in a past month.
  const monthFiltered = useMemo(() => {
    if (monthFilter === "all") return allRows;
    const target = monthFilter === "current" ? todayMonthKey() : nextMonthKey();
    return allRows.filter(
      (o) => monthKey(o.planned_delivery_date) === target || isOverdue(o),
    );
  }, [allRows, monthFilter, todayIso]);

  const overdueCount = useMemo(
    () => orders.filter((o) => isOverdue(o)).length,
    [orders, todayIso],
  );

  const nextMonthPendingCount = useMemo(
    () =>
      allRows.filter(
        (o) =>
          (o.status === "pending" || o.is_forecast) &&
          monthKey(o.planned_delivery_date) === nextMonthKey(),
      ).length,
    [allRows],
  );

  const counts = useMemo(
    () => ({
      pending: monthFiltered.filter((o) => o.status === "pending").length,
      shipped: monthFiltered.filter((o) => o.status === "shipped").length,
      forecast: monthFiltered.filter((o) => o.is_forecast).length,
      all: monthFiltered.length,
    }),
    [monthFiltered],
  );


  // Klick på ett packdatum-kort: visa bara just det datumet, oavsett månadsfilter.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = dateFilter
      ? allRows.filter((o) => o.planned_delivery_date === dateFilter)
      : monthFiltered;
    return base.filter((o) => {
      // Prognosrader följer med i "Väntande" så kommande månader syns.
      if (tab !== "all" && o.status !== tab && !(tab === "pending" && o.is_forecast)) return false;
      if (!q) return true;
      const p = profiles[o.user_id];
      const name = `${p?.first_name ?? ""} ${p?.last_name ?? ""}`.toLowerCase();
      return (
        name.includes(q) ||
        (p?.email ?? "").toLowerCase().includes(q) ||
        (p?.company ?? "").toLowerCase().includes(q)
      );
    });
  }, [monthFiltered, allRows, dateFilter, profiles, tab, search]);

  // Packvy: väntande (riktiga) leveranser grupperade per planerat datum.
  const packingByDate = useMemo(() => {
    const map = new Map<
      string,
      { date: string; orderCount: number; totalTubes: number; flavors: Record<string, number> }
    >();
    orders
      .filter((o) => o.status === "pending")
      .forEach((o) => {
        const d = o.planned_delivery_date ?? o.created_at.slice(0, 10);
        let g = map.get(d);
        if (!g) {
          g = { date: d, orderCount: 0, totalTubes: 0, flavors: {} };
          map.set(d, g);
        }
        g.orderCount += 1;
        (o.items ?? []).forEach((it) => {
          g!.flavors[it.product_id] = (g!.flavors[it.product_id] ?? 0) + Number(it.quantity);
          g!.totalTubes += Number(it.quantity);
        });
      });
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [orders]);


  const pendingInView = useMemo(
    () => filtered.filter((o) => o.status === "pending"),
    [filtered],
  );

  const allSelected =
    pendingInView.length > 0 && pendingInView.every((o) => selected.has(o.id));

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        pendingInView.forEach((o) => next.delete(o.id));
      } else {
        pendingInView.forEach((o) => next.add(o.id));
      }
      return next;
    });
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Packing list totals across pending orders currently in view
  const packingTotals = useMemo(() => {
    const totals: Record<string, { name: string; quantity: number }> = {};
    let totalTubes = 0;
    pendingInView.forEach((o) => {
      (o.items ?? []).forEach((it) => {
        const key = it.product_id;
        const name = it.name || it.product_id;
        if (!totals[key]) totals[key] = { name, quantity: 0 };
        totals[key].quantity += Number(it.quantity);
        totalTubes += Number(it.quantity);
      });
    });
    return {
      rows: Object.entries(totals).map(([id, v]) => ({ id, ...v })),
      totalTubes,
      orderCount: pendingInView.length,
    };
  }, [pendingInView]);

  // Samma siffror som i packvyn, men för packlistans aktuella urval —
  // grupperat per datum så utskriften matchar skärmen.
  const packingDatesInView = useMemo(() => {
    const map = new Map<
      string,
      { date: string; totalTubes: number; rows: Map<string, { name: string; quantity: number }> }
    >();
    pendingInView.forEach((o) => {
      const d = o.planned_delivery_date ?? o.created_at.slice(0, 10);
      let g = map.get(d);
      if (!g) {
        g = { date: d, totalTubes: 0, rows: new Map() };
        map.set(d, g);
      }
      (o.items ?? []).forEach((it) => {
        const cur = g!.rows.get(it.product_id) ?? {
          name: it.name || it.product_id,
          quantity: 0,
        };
        cur.quantity += Number(it.quantity);
        g!.rows.set(it.product_id, cur);
        g!.totalTubes += Number(it.quantity);
      });
    });
    return Array.from(map.values())
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((g) => ({
        date: g.date,
        totalTubes: g.totalTubes,
        rows: Array.from(g.rows.entries()).map(([id, v]) => ({ id, ...v })),
      }));
  }, [pendingInView]);

  const markShipped = async (orderIds: string[]) => {
    if (orderIds.length === 0) return;
    setBusy(true);
    setShippingIds((prev) => {
      const next = new Set(prev);
      orderIds.forEach((id) => next.add(id));
      return next;
    });
    try {
      const { data, error } = await supabase.functions.invoke(
        "admin-mark-delivery-shipped",
        { body: { order_ids: orderIds } },
      );
      if (error) throw error;
      const shipped = (data as { shipped?: number })?.shipped ?? 0;
      const errs = (data as { errors?: Array<{ order_id: string; error: string }> })?.errors ?? [];
      if (shipped > 0) {
        toast.success(
          shipped === 1
            ? "1 leverans markerad som skickad"
            : `${shipped} leveranser markerade som skickade`,
        );
      }
      if (errs.length > 0) {
        const requiresAction = errs.filter((e) => e.error === "payment_not_completed").length;
        const otherErrs = errs.length - requiresAction;
        if (requiresAction > 0) {
          toast.warning(
            `${requiresAction} order${requiresAction === 1 ? "" : "s"} kunde inte debiteras (kräver kundåtgärd / 3DS eller misslyckad). Kunden har fått e-post.`,
            { duration: 8000 },
          );
        }
        if (otherErrs > 0) {
          toast.error(
            `${otherErrs} order misslyckades med oväntat fel — se konsolen.`,
          );
          console.error("Ship errors:", errs);
        }
      }
      setSelected(new Set());
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Okänt fel";
      toast.error(`Kunde inte markera som skickad: ${msg}`);
    } finally {
      setBusy(false);
      setShippingIds((prev) => {
        const next = new Set(prev);
        orderIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  return (
    <AdminLayout title="Leveranser">
      <div className="space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sök kund…"
              className="pl-9"
            />
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setManualOrderOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Lägg till manuellt
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPackingOpen(true)}
              disabled={pendingInView.length === 0}
            >
              <ListChecks className="h-4 w-4 mr-2" />
              Generera packlista ({pendingInView.length})
            </Button>
            <Button
              size="sm"
              disabled={busy || selected.size === 0}
              onClick={() => markShipped(Array.from(selected))}
            >
              {busy && shippingIds.size > 1 ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Truck className="h-4 w-4 mr-2" />
              )}
              {busy && shippingIds.size > 1
                ? `Skickar ${shippingIds.size}…`
                : `Markera valda som skickade (${selected.size})`}
            </Button>
          </div>
        </div>

        {tab !== "flagged" && packingByDate.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide mb-2">
              Att packa
            </h2>
            <div className="flex gap-3 overflow-x-auto pb-1">
              {packingByDate.map((g) => {
                const overdue = g.date < todayIso;
                const active = dateFilter === g.date;
                return (
                  <button
                    key={g.date}
                    type="button"
                    onClick={() => setDateFilter(active ? null : g.date)}
                    className={cn(
                      "min-w-[220px] shrink-0 rounded-lg border p-3 text-left transition-colors",
                      active
                        ? "border-foreground bg-muted/60 ring-1 ring-foreground"
                        : "border-border bg-card hover:bg-muted/40",
                      overdue && !active && "border-red-300 bg-red-50/60",
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-semibold capitalize">{fmtDay(g.date)}</span>
                      {overdue && (
                        <span className="text-xs font-medium text-red-700">Försenad</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {g.orderCount} leverans{g.orderCount === 1 ? "" : "er"} · {g.totalTubes} tuber
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-1.5">
                      {Object.entries(g.flavors).map(([pid, qty]) => {
                        const f = FLAVOR_DOT[pid];
                        return (
                          <div
                            key={pid}
                            className="flex items-center gap-1.5 rounded border border-border/60 bg-background/60 px-2 py-1"
                          >
                            <span
                              className="h-3 w-3 rounded-full shrink-0"
                              style={{ backgroundColor: f?.color ?? "#94a3b8" }}
                            />
                            <span className="text-xs truncate">{f?.label ?? pid}</span>
                            <span className="ml-auto text-base font-bold leading-none">{qty}</span>
                          </div>
                        );
                      })}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
            <TabsList>
              <TabsTrigger value="pending">
                <Package className="h-3.5 w-3.5 mr-1.5" />
                Att skicka ({counts.pending}){counts.forecast > 0 ? ` +${counts.forecast} prognos` : ""}
              </TabsTrigger>
              <TabsTrigger value="shipped">
                <Truck className="h-3.5 w-3.5 mr-1.5" />
                Skickade ({counts.shipped})
              </TabsTrigger>
              <TabsTrigger value="all">Alla ({counts.all})</TabsTrigger>
              <TabsTrigger value="flagged" className="data-[state=active]:text-amber-900">
                <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
                Flaggade ({missingDeliveries.length + awaitingPayment.length + orphanDeliveries.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {tab !== "flagged" && (
            <Tabs value={monthFilter} onValueChange={(v) => setMonthFilter(v as MonthFilter)}>
              <TabsList>
                <TabsTrigger value="current">
                  <CalendarClock className="h-3.5 w-3.5 mr-1.5" />
                  Denna månad
                </TabsTrigger>
                <TabsTrigger value="next">Nästa månad</TabsTrigger>
                <TabsTrigger value="all">Alla månader</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </div>

        {tab === "flagged" ? (
          <FlaggedPanel
            missing={missingDeliveries}
            awaiting={awaitingPayment}
            orphans={orphanDeliveries}
            profiles={profiles}
          />
        ) : (
        <>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span>
            Visar:{" "}
            <span className="font-medium text-foreground">
              {dateFilter
                ? fmtDay(dateFilter)
                : monthFilter === "all"
                  ? "alla månader"
                  : fmtMonthLabel(
                      (monthFilter === "current" ? todayMonthKey() : nextMonthKey()) + "-01",
                    )}
            </span>
          </span>
          {dateFilter && (
            <button
              type="button"
              onClick={() => setDateFilter(null)}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 px-2 py-0.5 text-xs font-medium hover:bg-muted"
            >
              <X className="h-3 w-3" />
              Rensa datumfilter
            </button>
          )}
          {overdueCount > 0 && (
            <span className="text-red-700 font-medium">
              {overdueCount} försenad{overdueCount === 1 ? "" : "e"} leverans{overdueCount === 1 ? "" : "er"} visas alltid
            </span>
          )}
          {monthFilter !== "all" && (
            <span>
              Kommande månad: {nextMonthPendingCount} planerad{nextMonthPendingCount === 1 ? "" : "e"}
            </span>
          )}
        </div>
        <Card className="overflow-hidden">

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  {tab === "pending" && (
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      aria-label="Välj alla"
                    />
                  )}
                </TableHead>
                <TableHead>Kund</TableHead>
                <TableHead>Företag</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead>Betalning</TableHead>
                <TableHead>Paket</TableHead>
                <TableHead>Innehåll</TableHead>
                <TableHead>Adress</TableHead>
                <TableHead>Planerad leverans</TableHead>
                <TableHead>Skapad</TableHead>
                <TableHead>Status</TableHead>
                <TableHead aria-label="Åtgärd" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center text-sm text-muted-foreground py-10">
                    Laddar…
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center text-sm text-muted-foreground py-10">
                    Inga leveranser i den här vyn.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((o) => {
                  const p = profiles[o.user_id];
                  const name = p
                    ? [p.first_name, p.last_name].filter(Boolean).join(" ").trim()
                    : "";
                  const addr = o.shipping_address;
                  const isPending = o.status === "pending" && !o.is_forecast;
                  return (
                    <TableRow key={o.id} className={o.is_forecast ? "bg-muted/30 text-muted-foreground" : undefined}>
                      <TableCell>
                        {isPending && (
                          <Checkbox
                            checked={selected.has(o.id)}
                            onCheckedChange={() => toggleOne(o.id)}
                            aria-label={`Välj order ${o.id}`}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/admin/customers/${o.user_id}`}
                          className="hover:underline font-medium"
                        >
                          {name || p?.email || "—"}
                        </Link>
                        {p?.email && name && (
                          <div className="text-xs text-muted-foreground">{p.email}</div>
                        )}
                        {o.subscription_id && subs[o.subscription_id]?.nickname && (
                          <div className="mt-1">
                            <Badge variant="outline" className="text-[10px] font-normal">
                              {subs[o.subscription_id]!.nickname}
                            </Badge>
                          </div>
                        )}
                        {isPending &&
                          o.payment_status === "paid" &&
                          o.subscription_id &&
                          subs[o.subscription_id]?.status === "paused" && (
                            <div className="mt-1">
                              <Badge
                                variant="secondary"
                                className="bg-emerald-100 text-emerald-900 hover:bg-emerald-100 text-[10px] font-normal"
                              >
                                Pausad – redan betald, ska skickas
                              </Badge>
                            </div>
                          )}

                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {p?.company || "—"}
                      </TableCell>
                      <TableCell>
                        {o.order_mode === "subscription" ? (
                          <Badge variant="secondary" className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                            Prenumeration
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                            Engångsköp
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                           const ps = o.payment_status;
                           const invoiceVoided =
                             ps !== "paid" &&
                             (voidedOrderIds.has(o.id) ||
                               (!!o.subscription_id &&
                                 voidedSubMonths.has(
                                   `${o.subscription_id}|${monthKey(o.planned_delivery_date)}`,
                                 )));
                           const paidLabel = invoiceVoided
                             ? { text: "Faktura makulerad – ingen betalning väntas", cls: "text-rose-700" }
                             : ps === "paid"
                              ? { text: "Betald", cls: "text-emerald-700" }
                              : ps === "scheduled"
                                ? { text: "Planerad", cls: "text-slate-600" }
                                : ps === "awaiting_payment"
                                  ? { text: "Väntar betalning", cls: "text-amber-700" }
                                  : ps === "requires_action"
                                    ? { text: "Behöver åtgärd (3DS)", cls: "text-orange-700" }
                                    : ps === "failed"
                                      ? { text: "Misslyckad", cls: "text-red-700" }
                                      : { text: "Obetald", cls: "text-amber-700" };

                          // payment_method may be 'invoice', 'card', 'subscription' (legacy
                          // recurring placeholder), or null. Treat 'subscription' as card-style
                          // for now — it'll switch to real 'card' once auto-debit lands.
                          if (o.payment_method === "invoice") {
                            return (
                              <div className="flex flex-col gap-1">
                                <Badge
                                  variant="secondary"
                                  className="bg-amber-100 text-amber-900 hover:bg-amber-100 gap-1 w-fit"
                                >
                                  <FileText className="h-3 w-3" />
                                  Faktura
                                </Badge>
                                <span
                                  className={
                                    "text-[10px] uppercase tracking-wide font-medium " +
                                    paidLabel.cls
                                  }
                                >
                                  {paidLabel.text}
                                </span>
                              </div>
                            );
                          }
                          return (
                            <div className="flex flex-col gap-1">
                              <Badge
                                variant="secondary"
                                className="bg-sky-100 text-sky-900 hover:bg-sky-100 gap-1 w-fit"
                              >
                                <CreditCard className="h-3 w-3" />
                                Kort
                              </Badge>
                              <span
                                className={
                                  "text-[10px] uppercase tracking-wide font-medium " +
                                  paidLabel.cls
                                }
                              >
                                {paidLabel.text}
                              </span>
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-sm">{o.product_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {(o.items ?? [])
                          .map((it) => `${it.name || it.product_id} ×${it.quantity}`)
                          .join(", ") || "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {addr ? (
                          <div className="flex items-start gap-1">
                            <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                            <div>
                              <div>{addr.address || "—"}</div>
                              <div>
                                {addr.postalCode} {addr.city}
                              </div>
                            </div>
                          </div>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {o.planned_delivery_date ? (
                          <div className="flex flex-col gap-1">
                            <span
                              className={
                                isOverdue(o) ? "font-medium text-red-700" : "font-medium text-foreground"
                              }
                            >
                              {fmtDate(o.planned_delivery_date)}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              Levereras {deliveryWindowLabel(o.planned_delivery_date)}
                            </span>
                            {isOverdue(o) && (
                              <Badge className="bg-red-100 text-red-800 hover:bg-red-100 w-fit text-[10px]">
                                Försenad {Math.max(
                                  1,
                                  Math.round(
                                    (new Date(todayIso).getTime() -
                                      new Date(o.planned_delivery_date).getTime()) /
                                      86400000,
                                  ),
                                )} dgr
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>

                      <TableCell className="text-xs text-muted-foreground">
                        {fmtDate(o.created_at)}
                      </TableCell>
                      <TableCell>
                        {o.is_forecast ? (
                          <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">
                            Prognos
                          </Badge>
                        ) : (
                          <Badge className={badgeFor(o.status)}>{o.status}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {isPending && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            onClick={() => markShipped([o.id])}
                          >
                            {shippingIds.has(o.id) ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Skickar…
                              </>
                            ) : (
                              "Markera skickad"
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>
        </>
        )}
      </div>


      {/* Packing list modal */}
      <Dialog open={packingOpen} onOpenChange={setPackingOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto print:max-w-none print:max-h-none print:overflow-visible print:shadow-none">
          <DialogHeader>
            <DialogTitle>Packlista — {packingTotals.orderCount} leveranser</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 print:space-y-4" id="packing-list-print">
            {/* Summary box */}
            <div className="border border-border rounded-md p-4 bg-muted/40 print:bg-transparent">
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="text-sm font-semibold uppercase tracking-wide">
                  Sammanfattning
                </h3>
                <div className="text-sm text-muted-foreground">
                  Totalt antal tuber: <span className="text-foreground font-semibold">{packingTotals.totalTubes}</span>
                </div>
              </div>
              <div className="space-y-4">
                {packingDatesInView.map((g) => (
                  <div key={g.date}>
                    <div className="flex items-baseline justify-between mb-2">
                      <h4 className="font-semibold capitalize">{fmtDay(g.date)}</h4>
                      <span className="text-sm text-muted-foreground">{g.totalTubes} tuber</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {g.rows.map((r) => (
                        <div
                          key={r.id}
                          className="flex items-center gap-2 rounded-md border border-border px-3 py-2"
                        >
                          <span
                            className="h-3.5 w-3.5 rounded-full shrink-0"
                            style={{ backgroundColor: FLAVOR_DOT[r.id]?.color ?? "#94a3b8" }}
                          />
                          <span className="text-sm truncate">{FLAVOR_DOT[r.id]?.label ?? r.name}</span>
                          <span className="ml-auto text-lg font-bold leading-none">{r.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Per-order packing slips */}
            <div className="space-y-4 print:space-y-0">
              {pendingInView.map((o, idx) => {
                const p = profiles[o.user_id];
                const customerName = p
                  ? [p.first_name, p.last_name].filter(Boolean).join(" ").trim()
                  : "";
                const addr = o.shipping_address;
                const totalTubes = (o.items ?? []).reduce(
                  (sum, it) => sum + Number(it.quantity || 0),
                  0,
                );
                return (
                  <div
                    key={o.id}
                    className="border border-border rounded-md p-4 print:break-inside-avoid print:break-after-page print:rounded-none print:border-t-2 print:border-x-0 print:border-b-0"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="text-xs text-muted-foreground uppercase tracking-wide">
                          Order #{idx + 1} av {pendingInView.length}
                        </div>
                        <div className="font-semibold text-base mt-0.5">
                          {customerName || p?.email || "—"}
                        </div>
                        {p?.company && (
                          <div className="text-sm text-muted-foreground">{p.company}</div>
                        )}
                        {p?.email && customerName && (
                          <div className="text-xs text-muted-foreground">{p.email}</div>
                        )}
                      </div>
                      <Badge
                        variant="secondary"
                        className={
                          o.order_mode === "subscription"
                            ? "bg-blue-100 text-blue-800 hover:bg-blue-100"
                            : "bg-purple-100 text-purple-800 hover:bg-purple-100"
                        }
                      >
                        {o.order_mode === "subscription" ? "Prenumeration" : "Engångsköp"}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                          Leveransadress
                        </div>
                        {addr ? (
                          <div className="text-sm leading-relaxed">
                            {addr.name && <div>{addr.name}</div>}
                            <div>{addr.address || "—"}</div>
                            <div>
                              {addr.postalCode} {addr.city}
                            </div>
                            {addr.country && <div>{addr.country}</div>}
                          </div>
                        ) : (
                          <div className="text-sm text-muted-foreground">Ingen adress angiven</div>
                        )}
                      </div>
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                          Paket
                        </div>
                        <div className="text-sm">{o.product_name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Totalt {totalTubes} tuber
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                        Innehåll
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Produkt</TableHead>
                            <TableHead className="text-right">Antal</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(o.items ?? []).length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={2} className="text-sm text-muted-foreground">
                                Inga produkter angivna
                              </TableCell>
                            </TableRow>
                          ) : (
                            (o.items ?? []).map((it) => (
                              <TableRow key={`${o.id}-${it.product_id}`}>
                                <TableCell>{it.name || it.product_id}</TableCell>
                                <TableCell className="text-right font-medium">
                                  ×{it.quantity}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-2 print:hidden">
              <Button variant="outline" onClick={() => window.print()}>
                Skriv ut
              </Button>
              <Button onClick={() => setPackingOpen(false)}>Stäng</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <RecordEditDialog
        open={!!editOrder || manualOrderOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditOrder(null);
            setManualOrderOpen(false);
          }
        }}
        table="orders"
        title={editOrder ? "Redigera leverans" : "Lägg till leverans manuellt"}
        fields={editOrder ? orderFields : orderCreateFields}
        record={editOrder}
        onSaved={load}
      />
    </AdminLayout>
  );
};

export default AdminDeliveriesPage;
